import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";

import InviktusLogo from "@/components/InviktusLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, formatAuthError } from "@/contexts/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Fix 7 (B8/N16): /login?expirada=1&from=/ruta?query after an expired session
  const qs = new URLSearchParams(loc.search);
  const expired = qs.get("expirada") === "1";
  const fromQs = qs.get("from") || "";
  const safeFrom = (p) => (typeof p === "string" && p.startsWith("/") && !p.startsWith("//") ? p : "");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login({ email, password });
      toast.success(`Bienvenido, ${data.user?.name || data.user?.email}`);
      const dest = safeFrom(loc.state?.from) || safeFrom(fromQs) || "/dashboard";
      nav(dest, { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail ? formatAuthError(err.response.data.detail) : err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <div className="mx-auto flex min-h-screen max-w-7xl items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between">
            <Link to="/" className="inline-flex">
              <InviktusLogo size={42} tone="light" />
            </Link>
            <ThemeToggle />
          </div>

          <div className="mt-10 border border-white/15 bg-white/[0.04] p-8 backdrop-blur-xl"
               data-testid="login-card">
            <p className="eyebrow text-accent-blue">Acceso al portal</p>
            <h1 className="mt-2 font-display text-3xl text-white">
              Inicie sesión
            </h1>
            <p className="mt-2 text-sm text-slate-300/80">
              Use sus credenciales para acceder a la plataforma Inviktus.
            </p>

            {expired && (
              <p className="mt-4 border border-amber-300/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200" data-testid="login-expired">
                Su sesión expiró. Inicie sesión de nuevo para continuar donde estaba.
              </p>
            )}
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-slate-300/80">
                  Correo
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="mt-1 h-10 rounded-sm border-white/15 bg-white/5 text-white placeholder:text-slate-400 focus-visible:ring-accent-blue"
                  data-testid="login-email-input"
                  placeholder="admin@inviktus.com"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-slate-300/80">
                  Contraseña
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1 h-10 rounded-sm border-white/15 bg-white/5 text-white focus-visible:ring-accent-blue"
                  data-testid="login-password-input"
                />
              </div>

              {error ? (
                <div className="rounded-sm border border-red-400/30 bg-red-500/10 p-3 text-[12px] text-red-200"
                     data-testid="login-error">
                  {error}
                </div>
              ) : null}

              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-sm bg-gradient-to-r from-accent-blue to-accent-purple text-[11px] uppercase tracking-[0.2em] text-white hover:opacity-95"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <LogIn className="mr-2 h-3.5 w-3.5" />
                )}
                Ingresar
              </Button>
            </form>

            <div className="mt-6 flex items-center justify-between text-[12px] text-slate-300/80">
              <Link to="/" className="hover:text-white">← Volver al inicio</Link>
              <Link to="/register" className="text-accent-blue hover:underline"
                    data-testid="goto-register-link">
                Crear cuenta nueva
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
